import request from 'utils/request'
import { parseLocale } from 'utils/localization'
import { LOCAL_STORAGE_KEYS } from 'utils/constants'
import { setLocale } from 'store/locales'

import DEFAULT_STRINGS from '../strings/en.json'

const LINKT_WEB_APPLANGA_TAG_ID = '64ca99829e29a82e8489a8d5'

// ------------------------------------
// Constants
// ------------------------------------

const SET_STRINGS = 'SET_STRINGS'
export const DEFAULT_LANGUAGE = 'en'

// ------------------------------------
// Actions
// ------------------------------------

const setStrings = payload => ({
    type: SET_STRINGS,
    payload
  })

// ------------------------------------
// Utils
// ------------------------------------

const getStringValues = strings => {
  const keys = Object.keys(strings)
  const output = {}
  keys.forEach(key => {
    output[key] = strings[key].v
  })
  return output
}

const processFetchedStrings = ({
  fetchedData = {},
  language,
  localeId,
  isFail = false
}) => {
  const isResponseEmpty = Object.values(fetchedData).length === 0

  let stringsToSet = null
  let localeToSet = null

  if (!isResponseEmpty) {
    // If there are translations returned from Applanga we will set them
    const strings = getStringValues(fetchedData[language]?.main.entries)
    stringsToSet = strings
    localeToSet = localeId
  } else if (isFail || isResponseEmpty) {
    /**
     * If there are no translations returned from Applanga, we will first check
     * if there are any strings and locale saved in localStorage of the browser.
     */
    const localStrings = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEYS.strings)
    )
    /**
     * If both keys of `locale` and `strings` are in localStorage,
     * we will use the locally stored strings.
     */
    if (localStrings && localStrings.locale) {
      const { locale, strings } = localStrings
      stringsToSet = strings
      localeToSet = locale
    } else {
      /**
       * Otherwise we will default to English.
       */
      stringsToSet = DEFAULT_STRINGS
      localeToSet = DEFAULT_LANGUAGE
    }
  }
  return { stringsToSet, localeToSet }
}

const setStringsAndLocale = (strings, locale) => dispatch => {
  dispatch(setStrings(strings))
  dispatch(setLocale(locale))
}

const saveStringsAndLocaleToLocaleStorage = (strings, locale) => {
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.strings,
    JSON.stringify({
      strings,
      locale
    })
  )
}

// ------------------------------------
// Action Creators
// ------------------------------------

const onFetchApplangaStrings = (
  language = DEFAULT_LANGUAGE,
  localeId = DEFAULT_LANGUAGE
) => dispatch => {
    const { APPLANGA_AUTHORIZATION_HEADER } = process.env
    const appId = APPLANGA_AUTHORIZATION_HEADER
      ? APPLANGA_AUTHORIZATION_HEADER.split('!')[0]
      : ''
    if (!appId) {
      dispatch(setStrings(DEFAULT_STRINGS))
      dispatch(setLocale(DEFAULT_LANGUAGE))
      return
    }

    let languageParam = language
    if (language !== DEFAULT_LANGUAGE) {
      languageParam = parseLocale(language)
    }

    dispatch(
      request({
        method: 'GET',
        url: [
          'https://api.applanga.com/v1/api',
          `?app=${appId}`,
          '&requestedGroups=[%22main%22]',
          `&requestedLanguages=[%22${languageParam}%22]`,
          `&requestedTagIds=[%22${LINKT_WEB_APPLANGA_TAG_ID}%22]`
        ].join(''),
        headerOptions: {
          Authorization: `Bearer ${APPLANGA_AUTHORIZATION_HEADER}`
        },
        authRequired: false,
        bypassEnforcedLang: true,
        success: json => {
          const { data } = json
          const { stringsToSet, localeToSet } = processFetchedStrings({
            fetchedData: data,
            language,
            localeId
          })
          /**
           * Set the strings and locale to Redux.
           */
          dispatch(setStringsAndLocale(stringsToSet, localeToSet))
          /**
           * Save the strings and locale to localStorage for future use.
           */
          saveStringsAndLocaleToLocaleStorage(stringsToSet, localeToSet)
        },
        fail: () => {
          const { stringsToSet, localeToSet } = processFetchedStrings({
            language,
            localeId,
            isFail: true
          })
          /**
           * Set the strings and locale to Redux.
           */
          dispatch(setStringsAndLocale(stringsToSet, localeToSet))
          /**
           * Save the strings and locale to localStorage for future use.
           */
          saveStringsAndLocaleToLocaleStorage(stringsToSet, localeToSet)
        }
      })
    )
  }

const getDefaultStrings = () => {
  const localStrings = JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_KEYS.strings)
  )
  if (localStrings?.strings && localStrings?.locale) return localStrings.strings
  return DEFAULT_STRINGS
}

// ------------------------------------
// Reducer
// ------------------------------------

const stringsReducer = (state, action = {}) => {
  const currentState = state === undefined ? getDefaultStrings() : state

  switch (action.type) {
    case SET_STRINGS: {
      return action.payload || DEFAULT_STRINGS
    }
    default:
      return currentState
  }
}

export const stringsActions = {
  onFetchApplangaStrings
}

export default stringsReducer
